import React, { useState } from 'react';
import { Crop, Trash2, Upload } from 'lucide-react';
import { ImageCropModal } from './ImageCropModal';
import type { CropData } from './ImageCropModal';
import type { Area } from 'react-easy-crop';
import { getCroppedImageRenderProps, getCroppedImageStyles, hasCropApplied } from './cropUtils';

export type { CropData };

export type CropSlotMode = 'frame' | 'aspect';

function previewForSlot(crop: CropData | undefined, mode: CropSlotMode) {
  if (mode === 'aspect') {
    return getCroppedImageRenderProps(crop);
  }
  return {
    hasCrop: hasCropApplied(crop),
    aspectRatio: undefined as number | undefined,
    imageStyle: getCroppedImageStyles(crop),
  };
}

function CropPreviewBox({
  src,
  alt,
  crop,
  cropMode,
  lockedAspect,
  widthClass,
  fixedClass,
  className = '',
}: {
  src: string;
  alt: string;
  crop?: CropData;
  cropMode: CropSlotMode;
  lockedAspect?: number;
  widthClass: string;
  fixedClass: string;
  className?: string;
}) {
  const preview = previewForSlot(crop, cropMode);
  const boxAspect = lockedAspect ?? (cropMode === 'aspect' ? preview.aspectRatio : undefined);

  return (
    <div
      className={`${boxAspect ? widthClass : fixedClass} rounded-md overflow-hidden relative ${className}`}
      style={boxAspect ? { aspectRatio: boxAspect } : undefined}
    >
      <img
        src={src}
        alt={alt}
        className={
          preview.hasCrop
            ? cropMode === 'frame'
              ? 'absolute inset-0 h-full w-full'
              : undefined
            : 'w-full h-full object-cover'
        }
        style={preview.hasCrop ? preview.imageStyle : undefined}
      />
    </div>
  );
}

export interface ImageWithCropData {
  imageUrl?: string;
  cloudfrontUrl?: string;
  crop?: CropData;
}

interface ImageThumbnailWithCropProps {
  imageUrl?: string;
  cloudfrontUrl?: string;
  crop?: CropData;
  alt?: string;
  onUpload: () => void;
  onRemove: () => void;
  onCropChange: (crop: CropData, croppedAreaPixels: Area) => void;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  cropMode?: CropSlotMode;
  lockedAspect?: number;
}

export const ImageThumbnailWithCrop: React.FC<ImageThumbnailWithCropProps> = ({
  imageUrl,
  cloudfrontUrl,
  crop,
  alt = 'Image thumbnail',
  onUpload,
  onRemove,
  onCropChange,
  size = 'medium',
  className = '',
  cropMode = 'frame',
  lockedAspect,
}) => {
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  const displayUrl = cloudfrontUrl || imageUrl;

  const sizeClasses = {
    small: 'w-12 h-12',
    medium: 'w-16 h-16',
    large: 'w-24 h-24',
  };
  const widthClasses = {
    small: 'w-12',
    medium: 'w-16',
    large: 'w-24',
  };

  const handleCropApply = (cropData: CropData, croppedAreaPixels: Area) => {
    onCropChange(cropData, croppedAreaPixels);
    setIsCropModalOpen(false);
  };

  if (!displayUrl) {
    return (
      <button
        type="button"
        onClick={onUpload}
        className={`${sizeClasses[size]} border-2 border-dashed border-gray-300 rounded-md hover:border-primary-500 flex flex-col items-center justify-center text-gray-400 hover:text-primary-600 transition-colors ${className}`}
      >
        <Upload size={16} />
        <span className="text-xs mt-1">Upload</span>
      </button>
    );
  }

  return (
    <>
      <div className={`relative group ${className}`}>
        <CropPreviewBox
          src={displayUrl}
          alt={alt}
          crop={crop}
          cropMode={cropMode}
          lockedAspect={lockedAspect}
          widthClass={widthClasses[size]}
          fixedClass={sizeClasses[size]}
          className="border border-gray-300"
        />

        {/* Action buttons overlay */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-md flex items-center justify-center space-x-1">
          <button
            type="button"
            onClick={() => setIsCropModalOpen(true)}
            className="p-1.5 bg-white rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
            title="Crop / Adjust"
          >
            <Crop size={14} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="p-1.5 bg-white rounded-md text-red-600 hover:bg-red-50 transition-colors"
            title="Remove"
          >
            <Trash2 size={14} />
          </button>
        </div>

        {/* Crop indicator */}
        {hasCropApplied(crop) && (
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary-500 rounded-full flex items-center justify-center">
            <Crop size={10} className="text-white" />
          </div>
        )}
      </div>

      {/* Crop Modal */}
      {displayUrl && (
        <ImageCropModal
          isOpen={isCropModalOpen}
          onClose={() => setIsCropModalOpen(false)}
          imageUrl={displayUrl}
          initialCrop={crop}
          lockedAspect={lockedAspect}
          onApply={handleCropApply}
        />
      )}
    </>
  );
};

/**
 * A more comprehensive image editor with URL input, upload button, and crop
 */
interface ImageEditorWithCropProps {
  imageUrl?: string;
  cloudfrontUrl?: string;
  crop?: CropData;
  onUrlChange: (url: string) => void;
  onUpload: () => void;
  onRemove: () => void;
  onCropChange: (crop: CropData, croppedAreaPixels: Area) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  cropMode?: CropSlotMode;
  lockedAspect?: number;
}

export const ImageEditorWithCrop: React.FC<ImageEditorWithCropProps> = ({
  imageUrl,
  cloudfrontUrl,
  crop,
  onUrlChange,
  onUpload,
  onRemove,
  onCropChange,
  placeholder = 'https://example.com/image.jpg',
  label = 'Image',
  required = false,
  cropMode = 'frame',
  lockedAspect,
}) => {
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const displayUrl = cloudfrontUrl || imageUrl;

  const handleCropApply = (cropData: CropData, croppedAreaPixels: Area) => {
    onCropChange(cropData, croppedAreaPixels);
    setIsCropModalOpen(false);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && '*'}
      </label>

      {displayUrl ? (
        <div className="flex flex-col sm:flex-row items-start space-y-3 sm:space-y-0 sm:space-x-3">
          {/* Thumbnail with crop indicator */}
          <div className="relative group">
            <CropPreviewBox
              src={displayUrl}
              alt="Thumbnail"
              crop={crop}
              cropMode={cropMode}
              lockedAspect={lockedAspect}
              widthClass="w-20"
              fixedClass="w-20 h-20"
              className="border border-gray-300 bg-gray-100"
            />

            {/* Crop indicator badge */}
            {hasCropApplied(crop) && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center shadow-sm">
                <Crop size={12} className="text-white" />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex-1 space-y-2">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setIsCropModalOpen(true)}
                className="px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100 flex items-center space-x-1"
              >
                <Crop size={14} />
                <span>Crop / Adjust</span>
              </button>
              <button
                type="button"
                onClick={onUpload}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 flex items-center space-x-1"
              >
                <Upload size={14} />
                <span>Change</span>
              </button>
              <button
                type="button"
                onClick={onRemove}
                className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 flex items-center space-x-1"
              >
                <Trash2 size={14} />
                <span>Remove</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 truncate" title={displayUrl}>
              {displayUrl.substring(0, 50)}...
            </p>
          </div>
        </div>
      ) : (
        <div className="flex space-x-2">
          <input
            type="url"
            value={imageUrl || ''}
            onChange={e => onUrlChange(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder={placeholder}
          />
          <button
            type="button"
            onClick={onUpload}
            className="px-3 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-md flex items-center space-x-1"
          >
            <Upload size={16} />
            <span>Upload</span>
          </button>
        </div>
      )}

      {/* Crop Modal */}
      {displayUrl && (
        <ImageCropModal
          isOpen={isCropModalOpen}
          onClose={() => setIsCropModalOpen(false)}
          imageUrl={displayUrl}
          initialCrop={crop}
          lockedAspect={lockedAspect}
          onApply={handleCropApply}
        />
      )}
    </div>
  );
};
